import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiClient, setApiToken } from '../api/client';
import type { UserRole } from '../types/database';
import type { LoginResponse, UserProfile } from '../types/auth';
import type { AuthLoginResponse } from '../types/apiResponses';

interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  companyId: string | null;
  permissions: string[];
  firstName: string | null;
  lastName: string | null;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  accessToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    companyName: string | undefined,
    firstName?: string,
    lastName?: string,
    invitationToken?: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (updates: { first_name?: string; last_name?: string }) => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'auth_access_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessTokenState] = useState<string | null>(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

        if (!storedToken || !storedRefreshToken) {
          setIsLoading(false);
          return;
        }

        // Try to use the stored token
        setAccessTokenState(storedToken);
        setApiToken(storedToken);

        // Fetch user profile to verify token is still valid
        try {
          const profile: UserProfile = await apiClient.profile.get();
          setUser({
            id: profile.id,
            email: profile.email,
            role: profile.role,
            companyId: profile.company_id,
            permissions: profile.permissions || [],
            firstName: profile.first_name,
            lastName: profile.last_name,
          });
        } catch {
          // Token might be expired, try to refresh
          try {
            const refreshData: LoginResponse = await apiClient.auth.refreshToken({
              refreshToken: storedRefreshToken,
            });

            // Update tokens
            localStorage.setItem(TOKEN_KEY, refreshData.accessToken);
            localStorage.setItem(REFRESH_TOKEN_KEY, refreshData.refreshToken);
            setAccessTokenState(refreshData.accessToken);
            setApiToken(refreshData.accessToken);

            // Set user from refresh response
            setUser({
              id: refreshData.user.id,
              email: refreshData.user.email,
              role: refreshData.user.role,
              companyId: refreshData.user.companyId,
              permissions: refreshData.user.permissions,
              firstName: refreshData.user.firstName,
              lastName: refreshData.user.lastName,
            });
          } catch {
            // Refresh failed, clear everything
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            setAccessTokenState(null);
            setApiToken(null);
            setUser(null);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response: AuthLoginResponse = await apiClient.auth.login({ email, password });

      // Store tokens
      localStorage.setItem(TOKEN_KEY, response.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);

      // Update state
      setAccessTokenState(response.accessToken);
      setApiToken(response.accessToken);
      setUser({
        id: response.user.id,
        email: response.user.email,
        role: response.profile.role,
        companyId: response.profile.companyId,
        permissions: response.permissions,
        firstName: response.profile.firstName,
        lastName: response.profile.lastName,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (
    email: string,
    password: string,
    companyName: string | undefined,
    firstName?: string,
    lastName?: string,
    invitationToken?: string
  ) => {
    setIsLoading(true);
    try {
      const response: AuthLoginResponse = await apiClient.auth.signUp({
        email,
        password,
        companyName,
        firstName,
        lastName,
        invitationToken,
      });

      // Store tokens
      localStorage.setItem(TOKEN_KEY, response.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);

      // Update state
      setAccessTokenState(response.accessToken);
      setApiToken(response.accessToken);
      setUser({
        id: response.user.id,
        email: response.user.email,
        role: response.profile.role,
        companyId: response.profile.companyId,
        permissions: response.permissions,
        firstName: response.profile.firstName,
        lastName: response.profile.lastName,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await apiClient.auth.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear everything regardless of API call success
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      setAccessTokenState(null);
      setApiToken(null);
      setUser(null);
      setIsLoading(false);
    }
  };

  const updateProfile = async (updates: { first_name?: string; last_name?: string }) => {
    if (!user) throw new Error('Not authenticated');

    const updatedProfile: UserProfile = await apiClient.profile.update(updates);

    // Update local user state
    setUser({
      ...user,
      firstName: updatedProfile.first_name,
      lastName: updatedProfile.last_name,
    });
  };

  const hasRole = (role: UserRole): boolean => {
    return user?.role === role;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    return user ? roles.includes(user.role) : false;
  };

  const hasPermission = (permission: string): boolean => {
    return user ? user.permissions.includes(permission) : false;
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    return user ? permissions.some((perm) => user.permissions.includes(perm)) : false;
  };

  const hasAllPermissions = (permissions: string[]): boolean => {
    return user ? permissions.every((perm) => user.permissions.includes(perm)) : false;
  };

  const value: AuthContextType = {
    isAuthenticated: !!user,
    isLoading,
    user,
    accessToken,
    login,
    signUp,
    logout,
    updateProfile,
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
